import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants/";
import CityLandingPageAllCitiesKey from "@shared/modules/caching/CityLandingPageAllCitiesKey";
import CityLandingPageCacheKey from "@shared/modules/caching/CityLandingPageCacheKey";
import CityLandingPageCategoriesKey from "@shared/modules/caching/CityLandingPageCategoriesKey";
import Breadcrumbs from "@app-contexts/public/features/discovery/components/Breadcrumbs/Breadcrumbs.web";
import SEO from "@ui-application/other/SEO/SEO.web";
import Text from "@ui-kit/typography/Text/Text.web";
import EventCategorySlider, {
  CategoryPillButton,
} from "@/components/molecules/discover/EventCategorySlider";
import Footer from "@ui-application/navigation/footer/Footer/Footer.web";
import NavBarV2 from "@/components/molecules/navbars/NavBar/NavBarV2";
import EventSearch from "@/components/organisms/discover/EventSearch/EventSearch";
import EventDetailsCardDiscovery from "@/components/organisms/discover/EventsSwiper/EventDetailsCardDiscovery";
import LocationDropdown from "@/components/organisms/discover/LocationDropdown";
import { cleanFilterParams } from "@/components/organisms/s/events/filter-params";
import withQueryClient, {
  GetServerSidePropsContextWithQueryClient,
} from "@/gssp-subroutines/withQueryClient";
import lang from "@/helpers/lang";
import useLoadMore from "@/hooks/useLoadMore";
import { breakpoints } from "@/styles/constants";

import useMediaQuery from "@/hooks/useMediaQuery";

import Grid from "@ui-kit/layout/Grid/Grid.web";
import Box from "@ui-kit/layout/Box/Box.web";
import styled from "@emotion/styled";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { useTranslation } from "@shared/utils/internationalization";
import Link from "next/link";
import { useRouter } from "next/router";
import { PropsWithChildren, useCallback, useRef } from "react";
import { dehydrate } from "react-query";
import { PaginatorContract } from "@shared/services/api/types";
import PublicDiscoveryViewRepository from "@app-contexts/public/features/discovery/data/repositories/PublicDiscoveryViewRepository";
import {
  PublicDiscoveryViewPayloadType,
  PublicDiscoveryViewSerializer,
} from "@app-contexts/public/features/discovery/data/types/discovery";
import usePublicDiscoveryViewService from "@app-contexts/public/features/discovery/data/services/usePublicDiscoveryViewService";
import {
  createBreadcrumbSchema,
  JSONLDBreadcrumbListWithServerProps,
} from "@shared/utils/seo/breadcrumbs";
import {
  CityLandingPageSerializer,
  CityLandingPageCategorySerializer,
} from "@shared/modules/caching/types";

const StyledGrid = styled(Grid)`
  width: 100%;
  position: relative;
  grid-column-gap: 24px;
  grid-row-gap: 40px;

  @media only screen and (min-width: ${breakpoints.xs}px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media only screen and (min-width: ${breakpoints.sm}px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media only screen and (min-width: ${breakpoints.xl}px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const defaultQueryParams: PublicDiscoveryViewPayloadType & {
  page_size?: number;
} = {
  payment_type: PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
  purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
  page_size: 24,
};

/**
 * Landing page for the TopEvents events subset in a city_landing_page
 */
const TopEventsCityLandingPage = ({
  breadcrumbs,
  city,
  cityCategories,
  eventsData: initialEventsData,
}: InferGetServerSidePropsType<typeof getServerSideProps>): JSX.Element => {
  const router = useRouter();
  const { t } = useTranslation("discovery");
  const { citySlug } = router.query;

  const queryParams = cleanFilterParams(router.query);
  const { usePaginated } = usePublicDiscoveryViewService();
  const {
    data: eventsData,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = usePaginated({
    params: {
      ...defaultQueryParams,
      ...queryParams,
      point_location: `${city.city_center?.latitude},${city.city_center?.longitude},${city.city_radius}`,
      is_featured: true,
    },
    options: {
      initialData: {
        pages: [initialEventsData],
        pageParams: [null],
      },
      staleTime: 60 * 10000, // 10 minutes
    },
  });

  const locationDropdownScrollRef = useRef();

  const loadMore = useCallback(
    (entries) => {
      if (entries?.length) {
        const target = entries[0];
        if (
          target.isIntersecting &&
          !isFetchingNextPage &&
          !isLoading &&
          hasNextPage
        ) {
          fetchNextPage();
        }
      }
    },
    [isFetchingNextPage, hasNextPage, isLoading, fetchNextPage]
  );

  const loadMoreRef = useLoadMore(loadMore);

  return (
    <Box>
      <SEO
        title={`${t("Popular events in")} ${city.name}`}
        metaDescription={city.description}
        description={city.description}
      />
      <Box
        display="flex"
        mt="md"
        mb="xl"
        paddingBottom="2xs"
        backgroundColor="bg"
        position="sticky"
        // Hug navbar
        top={["calc(56px)", null, null, "calc(75px)"]}
        // Navbar z-index - 2. This allows modals spawned by the Navbar to sit on top of
        // this sticky container, while simultaneously allows for Navbar's children (ie. the EventSearch component) to go over this component
        zIndex="stickyBelow"
      >
        <Box ref={locationDropdownScrollRef}>
          <Box marginLeft="-16px" position="relative" zIndex={2}>
            <LocationDropdown
              selectedCitySlug={city.slug?.toLowerCase()}
              onSubmit={({ city, geocodeAddress }) => {
                if (city) {
                  router.push(`/discover/${city.slug}/`);
                } else if (geocodeAddress) {
                  router.push(`/s/events/${geocodeAddress}/`);
                }
              }}
            />
          </Box>
        </Box>
        <Box minWidth="0" display="flex" alignItems="center" flex={1}>
          <EventCategorySlider
            btnSize="sm"
            ButtonComponent={({ category }) => {
              return (
                <Link href={`/discover/${citySlug}/${category.id}`} passHref>
                  <CategoryPillButton
                    data-testid={`top-events-${category.id}`}
                    size="sm"
                  >
                    <Text color="inherit" mt="none">
                      {t(category.name, { ns: "constants" })}
                    </Text>
                  </CategoryPillButton>
                </Link>
              );
            }}
            customCategories={cityCategories}
          />
        </Box>
      </Box>

      <Box my="md">
        <Breadcrumbs crumbs={breadcrumbs.itemListElement} />
      </Box>

      <Box>
        <Box>
          <Text
            as="h2"
            fontSize={["md", null, "3xl", null, "4xl"]}
            color="inherit"
            fontWeight={700}
          >
            {t("Popular in")} {city.name}
          </Text>
        </Box>
        <StyledGrid my={8}>
          {eventsData.pages.map((page) => {
            return page.results.map((event) => {
              return (
                <EventDetailsCardDiscovery
                  key={event.slug}
                  discoveryItem={event}
                  pageName={`${city.name} | ${"Popular in"}`}
                />
              );
            });
          })}
          {isFetchingNextPage
            ? Array.from({ length: 6 }, (_, i) => {
                return (
                  <EventDetailsCardDiscovery
                    key={i}
                    isLoading
                    discoveryItem={null}
                    pageName={`${city.name} | ${"Popular in"}`}
                  />
                );
              })
            : null}
        </StyledGrid>
      </Box>
      {hasNextPage && <Box ref={loadMoreRef} />}
    </Box>
  );
};

const LayoutContainer = styled(Box)`
  margin: 0 20px;

  @media screen and (min-width: ${breakpoints.xs}px) {
    margin: 8px 16px;
  }

  @media screen and (min-width: ${breakpoints.sm}px) {
    margin: 0 20px;
  }

  @media screen and (min-width: ${breakpoints.md}px) {
    margin: 0 40px;
  }

  @media screen and (min-width: ${breakpoints.xl}px) {
    margin: 0 80px;
  }

  @media screen and (min-width: 1880px) {
    max-width: 1720px;
    margin: 0 auto;
  }
`;

const NavEventSearchContainer = styled(Box)`
  flex: 1;
  padding: 0 6px;
  display: flex;
  justify-content: flex-end;

  @media screen and (min-width: ${breakpoints.sm + 1}px) {
    padding: 0 16px;
    display: block;
  }
`;

const TopEventsCityLandingPageLayout: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [isSmallerThanSmInclusive] = useMediaQuery(
    `(max-width: ${breakpoints.sm}px)`
  );

  return (
    <Box>
      <NavBarV2>
        <NavEventSearchContainer>
          <EventSearch
            variant={isSmallerThanSmInclusive ? "icon-only" : "full"}
          />
        </NavEventSearchContainer>
      </NavBarV2>
      <LayoutContainer>{children}</LayoutContainer>
      <Footer />
    </Box>
  );
};

TopEventsCityLandingPage.getLayout = function getLayout(
  page: React.ReactElement
) {
  return (
    <TopEventsCityLandingPageLayout>{page}</TopEventsCityLandingPageLayout>
  );
};

export default TopEventsCityLandingPage;

type TopEventsCityLandingPageGSSPType = {
  breadcrumbs: JSONLDBreadcrumbListWithServerProps;
  city: CityLandingPageSerializer;
  cityCategories: CityLandingPageCategorySerializer[];
  eventsData: PaginatorContract<PublicDiscoveryViewSerializer>;
};

export const getServerSideProps: GetServerSideProps<TopEventsCityLandingPageGSSPType> =
  withQueryClient(
    async ({
      params,
      locale,
      query,
      queryClient,
    }: GetServerSidePropsContextWithQueryClient) => {
      const { citySlug: citySlugParam } = params;

      // If citySlugParam is not a string, return 404
      if (typeof citySlugParam !== "string") {
        return {
          notFound: true,
        };
      }

      try {
        // Get all cities from Redis and put it into our RQ cache so we can access it on client-side
        // We use Redis for this rather than an endpoint because we don't expect this data to be changing constantly.
        // Putting it on Redis reduces stress on the DB.
        const allCitiesCache = new CityLandingPageAllCitiesKey();
        const allCities = await allCitiesCache.getValue();
        queryClient.setQueryData(
          ["city_landing_page", "all_cities"],
          allCities
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }

      let city: CityLandingPageSerializer = null;
      try {
        const citySlug = citySlugParam ? citySlugParam.toLowerCase() : "";
        const cityCache = new CityLandingPageCacheKey(citySlug);
        city = await cityCache.getValue();

        if (!city) {
          return {
            notFound: true,
          };
        }

        const queryParams = cleanFilterParams(query);

        const discoveryViewRepo = new PublicDiscoveryViewRepository();

        const eventsData = await discoveryViewRepo.paginate({
          params: {
            ...defaultQueryParams,
            ...queryParams,
            // The difference between this and greater_city_area is that we use radius here where
            // greater_city_area uses the greater_city_radius.
            point_location: `${city.city_center?.latitude},${city.city_center?.longitude},${city.city_radius}`,
            is_featured: true,
          },
        });

        const breadcrumbs = createBreadcrumbSchema([
          {
            item: `discover/${citySlug}/`,
            name: city.name,
          },
          {
            item: `discover/${citySlug}/top-events/`,
            name: "Popular in {{ cityName }}",
            interpolation: { cityName: city.name },
          },
        ]);

        // Get the available categories for this city
        const cityCategoriesCache = new CityLandingPageCategoriesKey(citySlug);
        const cityCategoriesValue = await cityCategoriesCache.getValue();
        const cityCategories = cityCategoriesValue;

        return {
          props: {
            dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
            ...(await lang(locale, ["discovery", "homepage", "constants"])),
            breadcrumbs,
            city,
            cityCategories,
            eventsData,
          } as TopEventsCityLandingPageGSSPType,
        };
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return {
          redirect: {
            destination: "/500",
          },
          props: {} as TopEventsCityLandingPageGSSPType,
        };
      }
    }
  );
